import {
  MarkdownTableCell,
  MarkdownTableObjectCell,
} from '../../extensions/table/types';
import { MarkdownBlock } from './MarkdownBlock';

const alignment = {
  center: ':---:',
  default: '---',
  left: ':---',
  right: '---:',
};

export class MarkdownTable extends MarkdownBlock {
  id = '';

  constructor(
    protected header: MarkdownTableCell[] = [],
    protected rows: MarkdownTableCell[][] = [],
  ) {
    super();
  }

  #getCell(cell: MarkdownTableCell): MarkdownTableObjectCell {
    return typeof cell === 'string' ? { content: cell } : cell;
  }
  #getSeparatorAlignment(cell?: MarkdownTableCell) {
    if (typeof cell === 'string') return alignment.default;

    switch (cell?.align) {
      case 'center':
        return alignment.center;
      case 'right':
        return alignment.right;
      case 'left':
        return alignment.left;
      default:
        return alignment.default;
    }
  }
  #getClassName(cell: MarkdownTableCell) {
    if (typeof cell === 'string') return '';

    return cell.className ? `(.${cell.className})` : '';
  }
  #getSpan(cell: MarkdownTableCell) {
    const spans: string[] = [];
    const c = this.#getCell(cell);

    if (c.rowSpan) spans.push(`({rowSpan:${c.rowSpan}})`);
    if (c.colSpan) spans.push(`({colSpan:${c.colSpan}})`);

    return spans.join(' ');
  }
  #getStyle(cell: MarkdownTableCell) {
    if (typeof cell === 'string') return '';

    const styles: string[] = [];
    if (cell.style) styles.push(cell.style);
    if (cell.align) styles.push(`text-align: ${cell.align}`);

    const style = styles.join(';');

    return style ? `({style:${style}})` : '';
  }
  #getAttributes(cell: MarkdownTableCell) {
    return [
      this.#getClassName(cell),
      this.#getStyle(cell),
      this.#getSpan(cell),
    ].join(' ');
  }
  #parseCell(cell: MarkdownTableCell) {
    return typeof cell === 'string'
      ? cell.replaceAll('\n', '')
      : `${this.#getAttributes(cell)} ${cell.content.replaceAll('\n', '')}`;
  }
  #rowToString = (row: string[]) => ['', ...row, ''].join(' | ');
  #getColumnAlignment(index: number) {
    let sum = 0,
      i = 0;
    for (i = 0; i < this.header.length && sum <= index; i++) {
      sum += Number.parseInt(this.#getCell(this.header[i]).colSpan ?? '1', 10);
    }
    return this.#getCell(this.header[i - 1]).align;
  }

  /**
   * Add rows to the body
   */
  addRows(rows: MarkdownTableCell[][]) {
    this.rows.push(...rows);
  }

  /**
   * Removes every row from the body.
   */
  clear() {
    this.rows = [];
  }

  /**
   * The header allows an only row by now
   */
  setHeader(row: MarkdownTableCell[]) {
    this.header = row;
  }

  setId(id: string) {
    this.id = id;
  }

  toString(): string {
    const heading = this.#rowToString(
      this.header.map((current) => this.#parseCell(current)),
    );
    const alignments = this.#rowToString(
      this.header.map((current) => this.#getSeparatorAlignment(current)),
    );
    const rows = this.rows
      .map((current) =>
        this.#rowToString(
          current.map((cell, i) =>
            this.#parseCell({
              ...this.#getCell(cell),
              align: this.#getColumnAlignment(i),
            }),
          ),
        ),
      )
      .join('\n');

    return `${
      this.id ? `{ #${this.id} }` : ''
    }${heading}\n${alignments}\n${rows}`;
  }
}
